<script setup lang="ts">
import { bemBuilder } from "@chatfood/core-utils";
import { ref, computed } from "vue";
import {
  useToast,
  AtomLoadingSpinner,
  SatItemBuilder,
  type SatItemBuilderEmits,
} from "@chatfood/design-system";
import type { ItemBuilderProps } from "./props";
import type { ItemBuilderEmits } from "./emits";
import { useBusinessStore } from "~/stores/business";
import { useBasketStore } from "~/stores/basket";
import { useItemStore } from "~/stores/item";
import { useAnalytics, useBugReporter } from "#imports";
import { useLocale } from "~/composables/use-locale";

const props = defineProps<ItemBuilderProps>();
const emits = defineEmits<ItemBuilderEmits>();

const css = bemBuilder("item-builder");

const { toastError } = useToast();
const { report } = useBugReporter();
const { trackEvent } = useAnalytics();
const { t } = useLocale();

const business = useBusinessStore();
const basket = useBasketStore();
const itemStore = useItemStore();

const isAdding = ref(false);

const isLoading = computed(() => itemStore.isProcessing);

const item = computed(() => {
  const isRootItemVariant = itemStore.state.type === "variant";

  return {
    ...itemStore.state,
    modifierLists: itemStore.state.modifierLists.map((list) => ({
      ...list,
      modifiers: list.modifiers.map((modifier) => ({
        ...modifier,
        // Ensure inStock is true for variant items
        // since we don't have the field at this level
        inStock: isRootItemVariant ? true : modifier.inStock,
      })),
    })),
  };
});

async function onAddToBasket(payload: SatItemBuilderEmits["addToBasket"][0]) {
  isAdding.value = true;

  try {
    await basket.addItem({
      ...payload,
      menuId: props.menuId,
    });

    trackEvent({
      name: "addItemToBasket",
      payload: {
        id: payload.id,
        name: itemStore.state.name,
        quantity: payload.quantity,
        price: {
          value: payload.unitPrice,
          currency: basket.currency,
        },
      },
    });

    onClose();
  } catch (error) {
    report(error);
    toastError(t("components.item_modal.item_add_error_message"));
  } finally {
    isAdding.value = false;
  }
}

const onClose = () => {
  emits("close");
};
</script>

<template>
  <div :class="css('')">
    <div v-if="isLoading" :class="css('loading')">
      <AtomLoadingSpinner />
    </div>
    <SatItemBuilder
      v-else
      :item="item"
      :discount-applied="basket.discount"
      :item-display-view="business.menuDisplayLayout === 'grid' ? 'col' : 'row'"
      :is-cta-loading="isAdding"
      @add-to-basket="onAddToBasket"
      @close="onClose"
    />
  </div>
</template>

<style lang="scss" scoped>
.item-builder {
  position: relative;
  max-width: 100%;
  height: 100%;
  min-height: 400px;

  &__loading {
    max-width: 100%;
    width: 100%;
    height: 100dvh;
    display: flex;
    align-items: center;
    justify-content: center;

    @media (width > $breakpoint-sm) {
      width: 580px;
      height: 580px;
    }
  }
}
</style>
